import React, { useRef, useState, useEffect } from "react";
import {
  VictimsAndKillersContext,
  type Filter,
  type FilterType,
  type VictimsAndKillersContextValue,
} from "../context/VictimsAndKillersContext";
import { useRandomHexColor } from "../hooks/useRandomHexColor";
import { useVictims } from "../hooks/queries/useVictims";
import { useSerialKillerDetails } from "../hooks/queries/useSerialKillerDetails";
import {
  useInitialFiltersFromCookie,
  usePersistFiltersCookie,
} from "../hooks/useFiltersCookie";
import { useLoading } from "../hooks/useLoading";

export function VictimsAndKillersProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const randomHexColor = useRandomHexColor();

  const cookieFilters = useInitialFiltersFromCookie();
  const initialFilters: Filter[] = (cookieFilters as unknown as Filter[]) ?? [
    {
      id: 0,
      killerId: "69b6cb2edd01381a4ad46e42",
      color: "#8e1616",
      visible: true,
      type: "serialkiller",
      from: null,
      to: null,
      country: "United States",
      region: null,
    },
    {
      id: 1,
      killerId: null,
      color: "#393e46",
      visible: true,
      type: "missing",
      from: null,
      to: null,
      country: "United States",
      region: "Washington",
    },
  ];

  const nextId = Math.max(-1, ...initialFilters.map((r) => r.id)) + 1;
  const idRef = useRef(nextId);
  const [filters, setFilters] = useState<Filter[]>(initialFilters);

  usePersistFiltersCookie(filters);

  const { victimsByFilter, loadingByFilter } = useVictims(filters);
  const { serialKillerById, serialKillerLoadingById } =
    useSerialKillerDetails(filters);

  const { show: showLoading, hide: hideLoading } = useLoading();
  const initialLoadDoneRef = useRef(false);
  useEffect(() => {
    if (initialLoadDoneRef.current) return;
    if (!cookieFilters) {
      initialLoadDoneRef.current = true;
      return;
    }
    const anyLoading = Object.values(loadingByFilter).some(Boolean);
    if (anyLoading) {
      showLoading();
    } else {
      initialLoadDoneRef.current = true;
      hideLoading();
    }
  }, [loadingByFilter, cookieFilters, showLoading, hideLoading]);

  const updateFilter = (id: number, patch: Partial<Filter>) =>
    setFilters((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    );

  const value: VictimsAndKillersContextValue = {
    filters: filters,
    addFilter: () =>
      setFilters((prev) => [
        ...prev,
        {
          id: idRef.current++,
          killerId: null,
          color: randomHexColor(),
          visible: true,
          type: "serialkiller",
          from: null,
          to: null,
          country: "United States",
          region: null,
        },
      ]),
    removeFilter: (id) => setFilters((prev) => prev.filter((r) => r.id !== id)),
    setFilterColor: (id, color) => updateFilter(id, { color }),
    setFilterVisible: (id, visible) => updateFilter(id, { visible }),
    setFilterKiller: (id, killerId) => updateFilter(id, { killerId }),
    setFilterType: (id, type: FilterType) =>
      updateFilter(id, {
        type,
        // Clear killerId when leaving serialkiller mode
        killerId:
          type === "serialkiller"
            ? (filters.find((r) => r.id === id)?.killerId ?? null)
            : null,
      }),
    setFilterFrom: (id, from) => updateFilter(id, { from }),
    setFilterTo: (id, to) => updateFilter(id, { to }),
    setFilterCountry: (id, country) => updateFilter(id, { country }),
    setFilterRegion: (id, region) => updateFilter(id, { region }),
    clearFilters: () => setFilters([]),
    importFilters: (incoming: Filter[]) => {
      const maxId = Math.max(-1, ...incoming.map((r) => r.id));
      idRef.current = maxId + 1;
      setFilters(incoming);
    },

    victimsByFilter: victimsByFilter,
    loadingByFilter: loadingByFilter,
    serialKillerById,
    serialKillerLoadingById,
  };

  return (
    <VictimsAndKillersContext.Provider value={value}>
      {children}
    </VictimsAndKillersContext.Provider>
  );
}
